import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { admin, kb, superadmin } from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";

const BASE_TABS = ["Overview", "Users", "Proposals", "Knowledge Base", "Audit Log"];
const PROPOSAL_STATUSES = ["draft", "in_progress", "in_review", "submitted", "won", "lost", "archived"];
const STATUS_CLS = { draft: "pill-gray", in_progress: "pill-blue", in_review: "pill-orange", submitted: "pill-green", won: "pill-green", lost: "pill-red", archived: "pill-gray" };
const FALLBACK_ROLES = ["Administrator", "Proposal Manager", "Contributor", "Reviewer", "External Reviewer", "KB Manager", "Auditor"];

export default function AdminConsole() {
  const nav = useNavigate();
  const { user, hasPerm } = useAuth();
  const isFullAdmin = hasPerm("user.manage");
  const [tab, setTab] = useState(isFullAdmin ? "Overview" : "Knowledge Base");
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [props, setProps] = useState([]);
  const [audit, setAudit] = useState([]);
  const [kbData, setKbData] = useState({ skills: [], certs: [], naics: [], clearances: [], pp: [] });
  const [pending, setPending] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leadList, setLeadList] = useState([]);
  const [busy, setBusy] = useState("");
  const [modal, setModal] = useState(null); // {kind}
  const [draft, setDraft] = useState({});
  const [manageOrg, setManageOrg] = useState(null); // org whose users are being managed
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgUserDraft, setOrgUserDraft] = useState(null); // null = not adding

  const isSuper = !!user?.is_superadmin;
  // Knowledge-base-only managers (Proposal Manager, KB Manager) can reach this page
  // via kb.manage, but only ever see the Knowledge Base tab — everything else here
  // (users, org-wide proposals, audit log, platform admin) requires user.manage.
  const TABS = isFullAdmin ? (isSuper ? [...BASE_TABS, "Organizations", "Leads"] : BASE_TABS) : ["Knowledge Base"];

  const loadOverview = async () => setOv(await admin.overview().catch(() => null));
  const loadUsers = async () => {
    setUsers(await admin.users());
    setPending(await admin.pendingUsers().catch(() => []));
    try { setRoles((await admin.roles()).roles); } catch { /* ignore */ }
  };
  const loadTenants = async () => setTenants(await superadmin.tenants().catch(() => []));
  const loadLeads = async () => setLeadList(await superadmin.leads().catch(() => []));
  const loadProps = async () => setProps(await admin.proposals().catch(() => []));
  const loadAudit = async () => setAudit(await admin.audit(300).catch(() => []));
  const loadKb = async () => {
    const [s, c, n, cl, pp] = await Promise.all([
      kb.skills(), kb.certifications(), kb.naics(), kb.clearances(), kb.pastPerformance(),
    ]);
    setKbData({ skills: s, certs: c, naics: n, clearances: cl, pp });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isFullAdmin) { loadOverview(); loadUsers(); } }, []);
  // Super-admins need the org list available for the "Add User" org dropdown (Users tab).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isFullAdmin && isSuper && !tenants.length) loadTenants(); }, [isSuper]);
  useEffect(() => {
    if (tab === "Proposals" && !props.length) loadProps();
    if (tab === "Audit Log" && !audit.length) loadAudit();
    if (tab === "Knowledge Base" && !kbData.skills.length && !kbData.naics.length) loadKb();
    if (tab === "Organizations" && !tenants.length) loadTenants();
    if (tab === "Leads" && !leadList.length) loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ---- Join-request approvals ----
  const approve = async (u) => {
    setBusy(u.id);
    try { await admin.approveUser(u.id); await Promise.all([loadUsers(), loadOverview()]); }
    catch (e) { err(e, "Approve failed"); } finally { setBusy(""); }
  };
  const reject = async (u) => {
    if (!window.confirm(`Reject ${u.email}'s request to join?`)) return;
    setBusy(u.id);
    try { await admin.rejectUser(u.id); await loadUsers(); }
    catch (e) { err(e, "Reject failed"); } finally { setBusy(""); }
  };

  // ---- Organizations (super-admin) ----
  const createOrg = async () => {
    const name = window.prompt("New organization name:");
    if (!name || !name.trim()) return;
    try { await superadmin.createTenant(name.trim()); await loadTenants(); }
    catch (e) { err(e, "Create failed"); }
  };
  const deleteOrg = async (t) => {
    if (!window.confirm(`Delete organization "${t.name}" and ALL its data (${t.users} users, ${t.proposals} proposals)? This cannot be undone.`)) return;
    setBusy(t.id);
    try { await superadmin.deleteTenant(t.id); await loadTenants(); }
    catch (e) { err(e, "Delete failed"); } finally { setBusy(""); }
  };

  // ---- Cross-org user membership (super-admin) ----
  const openManage = async (t) => {
    setManageOrg(t); setOrgUsers([]); setOrgUserDraft(null);
    try { setOrgUsers(await superadmin.tenantUsers(t.id)); }
    catch (e) { err(e, "Could not load users"); }
  };
  const reloadManage = async () => {
    if (manageOrg) setOrgUsers(await superadmin.tenantUsers(manageOrg.id).catch(() => []));
  };
  const addUserToOrg = async () => {
    const d = orgUserDraft || {};
    if (!d.name || !d.email || (!d.send_welcome_email && !d.password)) return;
    setBusy("org-add");
    try {
      const payload = { name: d.name, email: d.email, role: d.role || "Contributor", send_welcome_email: !!d.send_welcome_email };
      if (!d.send_welcome_email) payload.password = d.password;
      const res = await superadmin.createUserInTenant(manageOrg.id, payload);
      setOrgUserDraft(null);
      await Promise.all([reloadManage(), loadTenants()]);
      if (res?.welcome_link) window.alert(`Email not configured (stub) — welcome link:\n${res.welcome_link}`);
    } catch (e) { err(e, "Add failed"); } finally { setBusy(""); }
  };
  const moveUserTo = async (u, tenantId) => {
    if (!tenantId || tenantId === manageOrg.id) return;
    const dest = tenants.find((t) => t.id === tenantId);
    if (!window.confirm(`Move ${u.email} to "${dest?.name}"? They'll be signed out and must sign in again.`)) return;
    setBusy(u.id);
    try {
      await superadmin.moveUser(u.id, { tenant_id: tenantId });
      // Refresh the Users tab too — a move can add/remove a user from the
      // viewer's own org, and that list was otherwise left stale until reload.
      await Promise.all([reloadManage(), loadTenants(), loadUsers()]);
    } catch (e) { err(e, "Move failed"); } finally { setBusy(""); }
  };

  const userName = useMemo(() => {
    const m = {}; users.forEach((u) => { m[u.id] = u.name || u.email; }); return m;
  }, [users]);
  const err = (e, f) => window.alert(e?.response?.data?.detail || f);
  const roleOptions = roles.length ? roles : FALLBACK_ROLES;

  // ---- Users ----
  const changeRole = async (u, role) => {
    setBusy(u.id);
    try { await admin.updateUser(u.id, { role }); await Promise.all([loadUsers(), loadOverview()]); }
    catch (e) { err(e, "Update failed"); } finally { setBusy(""); }
  };
  const toggleActive = async (u) => {
    setBusy(u.id);
    try { await admin.updateUser(u.id, { active: !(u.active !== false) }); await Promise.all([loadUsers(), loadOverview()]); }
    catch (e) { err(e, "Update failed"); } finally { setBusy(""); }
  };
  const delUser = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"? This cannot be undone.`)) return;
    setBusy(u.id);
    try { await admin.deleteUser(u.id); await Promise.all([loadUsers(), loadOverview()]); }
    catch (e) { err(e, "Delete failed"); } finally { setBusy(""); }
  };

  // ---- Proposals ----
  const changeProposalStatus = async (p, status) => {
    setBusy(p.id);
    try { await admin.updateProposal(p.id, { status }); await Promise.all([loadProps(), loadOverview()]); }
    catch (e) { err(e, "Update failed"); } finally { setBusy(""); }
  };
  const delProposal = async (p) => {
    if (!window.confirm(`Permanently delete proposal "${p.title}"? This cannot be undone.`)) return;
    setBusy(p.id);
    try { await admin.deleteProposal(p.id); await Promise.all([loadProps(), loadOverview()]); }
    catch (e) { err(e, "Delete failed"); } finally { setBusy(""); }
  };

  // ---- KB delete ----
  const delKb = async (kind, id) => {
    if (!window.confirm("Remove this item?")) return;
    const fn = { skill: kb.delSkill, cert: kb.delCert, naics: kb.delNaics, clearance: kb.delClearance, pp: kb.delPastPerformance }[kind];
    try { await fn(id); await Promise.all([loadKb(), loadOverview()]); }
    catch (e) { err(e, "Delete failed (KB Manager / Admin required)"); }
  };

  // ---- Generic create modal ----
  const SPECS = {
    user: {
      title: "Add User", testid: "modal-user",
      fields: [
        { k: "name", label: "Full name" },
        { k: "email", label: "Email", type: "email" },
        { k: "role", label: "Role", type: "select", options: roleOptions },
        // Super-admins choose the org; org admins create within their own org (no field).
        ...(isSuper ? [{ k: "tenant_id", label: "Organization", type: "select", options: tenants.map((t) => ({ value: t.id, label: t.name })) }] : []),
        { k: "send_welcome_email", label: "Send a welcome email to set password (recommended)", type: "checkbox" },
        { k: "password", label: "Temporary password (min 12 chars)", type: "password", hideIf: (d) => d.send_welcome_email },
      ],
      save: async (d) => {
        const payload = { name: d.name, email: d.email, role: d.role || "Contributor", send_welcome_email: !!d.send_welcome_email };
        if (!d.send_welcome_email) payload.password = d.password;
        return isSuper ? superadmin.createUserInTenant(d.tenant_id, payload) : admin.createUser(payload);
      },
      reload: () => Promise.all([loadUsers(), loadOverview()]),
    },
    skill: {
      title: "Add Skill", testid: "modal-skill",
      fields: [
        { k: "name", label: "Capability" },
        { k: "category", label: "Category / NAICS family" },
        { k: "years_experience", label: "Years of experience", type: "number" },
        { k: "staff_count", label: "Staff with skill", type: "number" },
      ],
      save: async (d) => kb.addSkill({ name: d.name, category: d.category || "", years_experience: Number(d.years_experience) || 0, staff_count: Number(d.staff_count) || 0 }),
      update: async (id, d) => kb.updateSkill(id, { name: d.name, category: d.category || "", years_experience: Number(d.years_experience) || 0, staff_count: Number(d.staff_count) || 0 }),
      reload: loadKb,
    },
    cert: {
      title: "Add Certification", testid: "modal-cert",
      fields: [
        { k: "name", label: "Name" },
        { k: "type", label: "Type", type: "select", options: ["corporate", "individual"] },
        { k: "holder", label: "Holder" },
        { k: "expiry", label: "Expiry" },
        { k: "status", label: "Status", type: "select", options: ["Active", "Renew Soon", "In Process", "Expired"] },
      ],
      save: async (d) => kb.addCert({ name: d.name, type: d.type || "corporate", holder: d.holder || "Org", expiry: d.expiry, status: d.status || "Active" }),
      update: async (id, d) => kb.updateCert(id, { name: d.name, type: d.type || "corporate", holder: d.holder || "Org", expiry: d.expiry, status: d.status || "Active" }),
      reload: loadKb,
    },
    naics: {
      title: "Add NAICS Code", testid: "modal-naics",
      fields: [{ k: "code", label: "NAICS Code" }, { k: "label", label: "Label" }],
      save: async (d) => kb.addNaics({ code: d.code, label: d.label || "" }),
      update: async (id, d) => kb.updateNaics(id, { code: d.code, label: d.label || "" }),
      reload: loadKb,
    },
    clearance: {
      title: "Add Clearance / Set-Aside", testid: "modal-clearance",
      fields: [
        { k: "name", label: "Name" },
        { k: "detail", label: "Detail" },
        { k: "status", label: "Status", type: "select", options: ["Active", "In Process", "Expired"] },
      ],
      save: async (d) => kb.addClearance({ name: d.name, detail: d.detail || "", status: d.status || "Active" }),
      update: async (id, d) => kb.updateClearance(id, { name: d.name, detail: d.detail || "", status: d.status || "Active" }),
      reload: loadKb,
    },
    pp: {
      title: "Add Past Performance", testid: "modal-pp",
      fields: [
        { k: "title", label: "Contract title" },
        { k: "agency", label: "Agency" },
        { k: "narrative", label: "Narrative", type: "textarea" },
        { k: "tags", label: "Tags (comma-separated)" },
      ],
      save: async (d) => kb.addPastPerformance({ title: d.title, agency: d.agency || "", narrative: d.narrative || "", tags: (d.tags || "").split(",").map((t) => t.trim()).filter(Boolean) }),
      update: async (id, d) => kb.updatePastPerformance(id, { title: d.title, agency: d.agency || "", narrative: d.narrative || "", tags: (d.tags || "").split(",").map((t) => t.trim()).filter(Boolean) }),
      reload: loadKb,
    },
  };

  const openModal = (kind) => {
    // Default the org picker to the admin's own org (what this Users tab actually
    // lists) — picking a different org is still possible, but no longer the
    // silent-by-default footgun that made created users "disappear."
    setDraft(kind === "user" ? { send_welcome_email: true, ...(isSuper ? { tenant_id: user?.tenant_id || "" } : {}) } : {});
    setModal({ kind, editId: null });
  };
  const openEdit = (kind, item) => {
    const d = {};
    SPECS[kind].fields.forEach((f) => { d[f.k] = item[f.k] ?? ""; });
    if (kind === "pp") d.tags = (item.tags || []).join(", ");
    setDraft(d);
    setModal({ kind, editId: item.id });
  };
  const saveModal = async () => {
    const { kind, editId } = modal;
    const spec = SPECS[kind];
    const required = spec.fields[0].k;
    if (!draft[required]) return;
    if (kind === "user" && isSuper && !draft.tenant_id) { window.alert("Select an organization for this user."); return; }
    if (kind === "user" && !draft.send_welcome_email && !draft.password) { window.alert("Enter a temporary password, or check \"Send a welcome email\"."); return; }
    setBusy("modal");
    try {
      let res;
      if (editId && spec.update) await spec.update(editId, draft);
      else res = await spec.save(draft);
      await spec.reload(); setModal(null); setDraft({});
      if (kind === "user") {
        const msgs = [];
        // The Users tab only ever lists the viewer's own org (no cross-org
        // switcher here) — if a super-admin targeted a different org, the new
        // user is real but won't appear in this list. Say so explicitly instead
        // of leaving it to look like the user vanished.
        if (isSuper && draft.tenant_id && draft.tenant_id !== user?.tenant_id) {
          const orgName = tenants.find((t) => t.id === draft.tenant_id)?.name || "that organization";
          msgs.push(`User created in "${orgName}" — it won't show in this Users tab (which lists only your own org). Find it under Organizations → Manage users → ${orgName}.`);
        }
        if (res?.welcome_link) msgs.push(`Email not configured (stub) — welcome link:\n${res.welcome_link}`);
        if (msgs.length) window.alert(msgs.join("\n\n"));
      }
    } catch (e) { err(e, "Save failed"); } finally { setBusy(""); }
  };

  const downloadCsv = (rows, filename) => {
    // Neutralize spreadsheet formula injection: a cell starting with = + - @
    // executes as a formula when the CSV is opened in Excel/Sheets.
    const safe = (c) => {
      const s = String(c);
      return /^[=+\-@]/.test(s) ? `'${s}` : s;
    };
    const csv = rows.map((r) => r.map((c) => `"${safe(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url; link.download = filename;
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(url);
  };
  const exportAuditCsv = () => {
    const rows = [["timestamp", "actor", "action", "resource", "proposal_id"]];
    audit.forEach((a) => rows.push([a.ts || "", a.actor_email || a.actor_id || "", a.action || "", a.resource || "", a.proposal_id || ""]));
    downloadCsv(rows, "audit_log.csv");
  };
  const exportLeadsCsv = () => {
    const rows = [["email", "source", "created_at"]];
    leadList.forEach((l) => rows.push([l.email || "", l.source || "", l.created_at || ""]));
    downloadCsv(rows, "leads.csv");
  };

  const coverage = (p) => {
    const reqs = p.requirements || [];
    return reqs.length ? Math.round(reqs.reduce((a, r) => a + (r.coverage || 0), 0) / reqs.length) : 0;
  };

  const KbSection = ({ title, kind, items, cols, row }) => (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">{title} · {items.length}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => openModal(kind === "skill" ? "skill" : kind)} data-testid={`kb-add-${kind}`}>+ Add</button>
      </div>
      <table className="data-table">
        <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}<th></th></tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id}>
              {row(it).map((cell, i) => <td key={i}>{cell}</td>)}
              <td>
                <div className="flex gap-8">
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(kind, it)} data-testid={`kb-edit-${kind}-${it.id}`}>Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }} onClick={() => delKb(kind, it.id)} data-testid={`kb-del-${kind}-${it.id}`}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={cols.length + 1} style={{ color: "var(--text-muted)", textAlign: "center", padding: 16 }}>None yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="page" data-testid="page-admin-console">
      <div className="page-header">
        <div>
          <div className="page-title">{isFullAdmin ? "Administrator Console" : "Knowledge Base"}</div>
          <div className="page-subtitle">
            {isFullAdmin
              ? "Full org-wide CRUD · users, proposals, knowledge base, and audit"
              : "Manage skills, certifications, NAICS codes, clearances & set-asides, and past performance"}
          </div>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <div key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)} data-testid={`admin-tab-${t.replace(/\s/g, "-").toLowerCase()}`}
            style={{ padding: "10px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13, borderBottom: tab === t ? "2px solid var(--primary)" : "2px solid transparent", color: tab === t ? "var(--primary)" : "var(--text-secondary)" }}>
            {t}
          </div>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "Overview" && (
        ov ? (
          <>
            <div className="stats-row">
              <div className="stat-card blue"><div className="stat-label">Users</div><div className="stat-value">{ov.users.total}</div><div className="stat-meta">{ov.users.active} active · {ov.users.inactive} inactive · {ov.users.unverified} unverified</div><div className="stat-icon">👥</div></div>
              <div className="stat-card green"><div className="stat-label">Proposals</div><div className="stat-value">{ov.proposals.total}</div><div className="stat-meta">{Object.entries(ov.proposals.by_status).map(([k, v]) => `${v} ${k}`).join(" · ") || "—"}</div><div className="stat-icon">📑</div></div>
              <div className="stat-card orange"><div className="stat-label">Opportunities</div><div className="stat-value">{ov.opportunities.total}</div><div className="stat-meta">{ov.opportunities.sam} from SAM.gov</div><div className="stat-icon">🔍</div></div>
              <div className="stat-card purple"><div className="stat-label">Knowledge Base</div><div className="stat-value">{ov.knowledge_base.skills + ov.knowledge_base.certifications}</div><div className="stat-meta">{ov.knowledge_base.skills} skills · {ov.knowledge_base.certifications} certs · {ov.knowledge_base.past_performance} PP</div><div className="stat-icon">🎖</div></div>
              {ov.plan && (
                <div className="stat-card blue" data-testid="admin-plan-card">
                  <div className="stat-label">Plan</div>
                  <div className="stat-value">{ov.plan.label}</div>
                  <div className="stat-meta">
                    {ov.plan.seats_used}/{ov.plan.seats_cap ?? "∞"} seats · {ov.plan.proposals_used}/{ov.plan.proposals_cap ?? "∞"} proposals this month
                  </div>
                  <div className="stat-icon">💳</div>
                </div>
              )}
            </div>
            <div className="grid-2">
              <div className="card">
                <div className="card-header"><span className="card-title">Users by Role</span></div>
                <div style={{ padding: "12px 20px" }}>
                  {Object.entries(ov.users.by_role).map(([r, n]) => (
                    <div key={r} className="flex items-center justify-between" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ fontSize: 13 }}>{r}</span><span className="pill pill-blue">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-header"><span className="card-title">Recent Activity</span><button className="btn btn-ghost btn-sm" onClick={() => setTab("Audit Log")}>View all</button></div>
                <div style={{ padding: "8px 16px", maxHeight: 320, overflow: "auto" }}>
                  {(ov.recent_activity || []).map((a, i) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: 12.5 }}>
                      <div><strong>{a.actor_email || "system"}</strong> · {a.action}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: 11 }}>{a.resource} · {a.ts ? new Date(a.ts).toLocaleString() : ""}</div>
                    </div>
                  ))}
                  {!(ov.recent_activity || []).length && <div style={{ fontSize: 12, color: "var(--text-muted)", padding: 12 }}>No activity yet.</div>}
                </div>
              </div>
            </div>
          </>
        ) : <div className="center-load"><span className="spinner" /> Loading…</div>
      )}

      {/* USERS */}
      {tab === "Users" && (
        <>
        {pending.length > 0 && (
          <div className="card" data-testid="admin-pending-panel" style={{ marginBottom: 16, borderColor: "var(--warning)" }}>
            <div className="card-header"><span className="card-title">⏳ Join requests awaiting approval · {pending.length}</span></div>
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Requested</th><th></th></tr></thead>
              <tbody>
                {pending.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td style={{ color: "var(--text-secondary)" }}>{u.email}</td>
                    <td style={{ fontSize: 12 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td>
                      <div className="flex gap-8">
                        <button className="btn btn-primary btn-sm" disabled={busy === u.id} onClick={() => approve(u)} data-testid={`approve-${u.email}`}>Approve</button>
                        <button className="btn btn-ghost btn-sm" disabled={busy === u.id} onClick={() => reject(u)} style={{ color: "var(--danger)" }} data-testid={`reject-${u.email}`}>Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="card" data-testid="admin-users-panel">
          <div className="card-header">
            <span className="card-title">Users · {users.length}</span>
            <button className="btn btn-primary btn-sm" onClick={() => openModal("user")} data-testid="add-user-btn">+ Add User</button>
          </div>
          <table className="data-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Verified</th><th></th></tr></thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === user?.id;
                return (
                  <tr key={u.id} style={{ opacity: u.active === false ? 0.5 : 1 }}>
                    <td>{u.name}{isSelf && <span className="pill pill-blue" style={{ marginLeft: 6, fontSize: 10 }}>you</span>}</td>
                    <td style={{ color: "var(--text-secondary)" }}>{u.email}</td>
                    <td>
                      <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={busy === u.id || isSelf} data-testid={`admin-role-${u.email}`}>
                        {(roles.length ? roles : [u.role]).map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td><span className={`pill ${u.active === false ? "pill-red" : "pill-green"}`}>{u.active === false ? "Inactive" : "Active"}</span></td>
                    <td>{u.email_verified ? "✅" : "—"}</td>
                    <td>
                      <div className="flex gap-8">
                        <button className="btn btn-ghost btn-sm" disabled={busy === u.id || isSelf} onClick={() => toggleActive(u)}>{u.active === false ? "Reactivate" : "Deactivate"}</button>
                        <button className="btn btn-ghost btn-sm" disabled={busy === u.id || isSelf} onClick={() => delUser(u)} style={{ color: isSelf ? undefined : "var(--danger)" }} data-testid={`admin-deluser-${u.email}`}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* PROPOSALS */}
      {tab === "Proposals" && (
        <div className="card" data-testid="admin-proposals-panel">
          <div className="card-header">
            <span className="card-title">All Proposals · {props.length}</span>
            <button className="btn btn-primary btn-sm" onClick={() => nav("/new-response")} data-testid="add-proposal-btn">+ New Response</button>
          </div>
          <table className="data-table">
            <thead><tr><th>Title</th><th>Agency</th><th>Owner</th><th>Status</th><th>Members</th><th>Coverage</th><th></th></tr></thead>
            <tbody>
              {props.map((p) => (
                <tr key={p.id}>
                  <td className="font-600">{p.title}</td>
                  <td style={{ color: "var(--text-secondary)" }}>{p.agency || "—"}</td>
                  <td style={{ color: "var(--text-secondary)" }}>{userName[p.owner_id] || "—"}</td>
                  <td>
                    <select value={PROPOSAL_STATUSES.includes(p.status) ? p.status : "draft"} onChange={(e) => changeProposalStatus(p, e.target.value)} disabled={busy === p.id} data-testid={`admin-pstatus-${p.id}`}>
                      {PROPOSAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>{(p.members || []).length}</td>
                  <td><span className={`pill ${STATUS_CLS[p.status] || "pill-gray"}`}>{coverage(p)}%</span></td>
                  <td>
                    <div className="flex gap-8">
                      <button className="btn btn-ghost btn-sm" onClick={() => nav(`/workspace?proposal=${p.id}`)} data-testid={`admin-open-${p.id}`}>Open</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => delProposal(p)} disabled={busy === p.id} style={{ color: "var(--danger)" }} data-testid={`admin-del-${p.id}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!props.length && <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text-muted)", padding: 20 }}>No proposals.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* KNOWLEDGE BASE */}
      {tab === "Knowledge Base" && (
        <div data-testid="admin-kb-panel">
          <KbSection title="Skills" kind="skill" items={kbData.skills} cols={["Name", "Years", "Staff", "Proficiency"]}
            row={(s) => [s.name, s.years_experience ?? "—", s.staff_count ?? "—", s.proficiency || s.tier || "—"]} />
          <KbSection title="Certifications" kind="cert" items={kbData.certs} cols={["Name", "Type", "Holder", "Status"]}
            row={(c) => [c.name, c.type || "—", c.holder, c.status]} />
          <KbSection title="NAICS Codes" kind="naics" items={kbData.naics} cols={["Code", "Label"]}
            row={(n) => [n.code, n.label || "—"]} />
          <KbSection title="Clearances & Set-Asides" kind="clearance" items={kbData.clearances} cols={["Name", "Detail", "Status"]}
            row={(c) => [c.name, c.detail || "—", c.status]} />
          <KbSection title="Past Performance" kind="pp" items={kbData.pp} cols={["Title", "Agency", "Tags"]}
            row={(p) => [p.title, p.agency || "—", (p.tags || []).join(", ")]} />
        </div>
      )}

      {/* AUDIT LOG */}
      {tab === "Audit Log" && (
        <div className="card" data-testid="admin-audit-panel">
          <div className="card-header">
            <span className="card-title">Global Audit Log · {audit.length}</span>
            <button className="btn btn-ghost btn-sm" onClick={exportAuditCsv} disabled={!audit.length} data-testid="admin-audit-csv">⬇ Export CSV</button>
          </div>
          <div style={{ maxHeight: 560, overflow: "auto" }}>
            <table className="data-table">
              <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Resource</th></tr></thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{a.ts ? new Date(a.ts).toLocaleString() : "—"}</td>
                    <td style={{ fontSize: 12 }}>{a.actor_email || a.actor_id || "system"}</td>
                    <td><span className="pill pill-gray" style={{ fontSize: 11 }}>{a.action}</span></td>
                    <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>{a.resource}</td>
                  </tr>
                ))}
                {!audit.length && <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-muted)", padding: 20 }}>No audit entries.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ORGANIZATIONS (super-admin) */}
      {tab === "Organizations" && isSuper && (
        <div className="card" data-testid="admin-orgs-panel">
          <div className="card-header">
            <span className="card-title">Organizations · {tenants.length}</span>
            <button className="btn btn-primary btn-sm" onClick={createOrg} data-testid="add-org-btn">+ New Organization</button>
          </div>
          <table className="data-table">
            <thead><tr><th>Organization</th><th>Users</th><th>Proposals</th><th>Pending</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id}>
                  <td className="font-600">{t.name}{user?.tenant_id === t.id && <span className="pill pill-blue" style={{ marginLeft: 6, fontSize: 10 }}>your org</span>}</td>
                  <td>{t.users}</td>
                  <td>{t.proposals}</td>
                  <td>{t.pending ? <span className="pill pill-orange">{t.pending}</span> : "—"}</td>
                  <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                  <td>
                    <div className="flex gap-8">
                      <button className="btn btn-ghost btn-sm" onClick={() => openManage(t)} data-testid={`manage-org-${t.id}`}>Manage users</button>
                      <button className="btn btn-ghost btn-sm" disabled={busy === t.id || user?.tenant_id === t.id} onClick={() => deleteOrg(t)} style={{ color: user?.tenant_id === t.id ? undefined : "var(--danger)" }} data-testid={`del-org-${t.id}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!tenants.length && <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--text-muted)", padding: 20 }}>No organizations.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* LEADS (super-admin): demo requests captured on the public landing page */}
      {tab === "Leads" && isSuper && (
        <div className="card" data-testid="admin-leads-panel">
          <div className="card-header">
            <span className="card-title">Demo Requests · {leadList.length}</span>
            <button className="btn btn-ghost btn-sm" onClick={exportLeadsCsv} disabled={!leadList.length} data-testid="admin-leads-csv">⬇ Export CSV</button>
          </div>
          <table className="data-table">
            <thead><tr><th>Email</th><th>Source</th><th>Captured</th></tr></thead>
            <tbody>
              {leadList.map((l) => (
                <tr key={l.id || l.email}>
                  <td className="font-600">{l.email}</td>
                  <td><span className="pill pill-gray">{l.source || "landing"}</span></td>
                  <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {!leadList.length && <tr><td colSpan={3} style={{ textAlign: "center", color: "var(--text-muted)", padding: 20 }}>No demo requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* MANAGE ORG USERS (super-admin): add a user to this org, or move users between orgs */}
      {manageOrg && (
        <div className="modal-backdrop" onClick={() => setManageOrg(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, width: "94%" }} data-testid="manage-org-modal">
            <h3>Manage users · {manageOrg.name}</h3>
            <div className="modal-sub">Add a user directly to this organization, or move an existing user to another one.</div>

            <div className="card" style={{ marginTop: 12 }}>
              <table className="data-table">
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Move to org</th></tr></thead>
                <tbody>
                  {orgUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{u.name}{u.is_superadmin && <span className="pill pill-blue" style={{ marginLeft: 6, fontSize: 10 }}>super</span>}</td>
                      <td style={{ color: "var(--text-secondary)" }}>{u.email}</td>
                      <td>{u.role}</td>
                      <td>
                        <select
                          value=""
                          disabled={busy === u.id || u.is_superadmin || u.id === user?.id}
                          onChange={(e) => moveUserTo(u, e.target.value)}
                          data-testid={`move-user-${u.email}`}
                        >
                          <option value="">{u.is_superadmin || u.id === user?.id ? "—" : "Move to…"}</option>
                          {tenants.filter((t) => t.id !== manageOrg.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {!orgUsers.length && <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-muted)", padding: 16 }}>No users in this organization.</td></tr>}
                </tbody>
              </table>
            </div>

            {orgUserDraft ? (
              <div className="card" style={{ marginTop: 12, padding: 16 }} data-testid="org-add-user-form">
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Add user to {manageOrg.name}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div><label>Full name</label><input value={orgUserDraft.name || ""} onChange={(e) => setOrgUserDraft({ ...orgUserDraft, name: e.target.value })} data-testid="org-user-name" /></div>
                  <div><label>Email</label><input type="email" value={orgUserDraft.email || ""} onChange={(e) => setOrgUserDraft({ ...orgUserDraft, email: e.target.value })} data-testid="org-user-email" /></div>
                  <div>
                    <label>Role</label>
                    <select value={orgUserDraft.role || ""} onChange={(e) => setOrgUserDraft({ ...orgUserDraft, role: e.target.value })} data-testid="org-user-role">
                      <option value="">Contributor</option>
                      {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  {!orgUserDraft.send_welcome_email && (
                    <div><label>Temporary password (min 12 chars)</label><input type="password" value={orgUserDraft.password || ""} onChange={(e) => setOrgUserDraft({ ...orgUserDraft, password: e.target.value })} data-testid="org-user-password" /></div>
                  )}
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400, margin: "10px 0 0" }}>
                  <input type="checkbox" checked={!!orgUserDraft.send_welcome_email} onChange={(e) => setOrgUserDraft({ ...orgUserDraft, send_welcome_email: e.target.checked })} style={{ width: "auto" }} data-testid="org-user-welcome" />
                  Send a welcome email to set password (recommended)
                </label>
                <div className="flex gap-8" style={{ marginTop: 14, justifyContent: "flex-end" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setOrgUserDraft(null)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" disabled={busy === "org-add" || !orgUserDraft.name || !orgUserDraft.email || (!orgUserDraft.send_welcome_email && !orgUserDraft.password)} onClick={addUserToOrg} data-testid="org-user-save">{busy === "org-add" ? "Adding…" : "Add user"}</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-8" style={{ marginTop: 14, justifyContent: "space-between" }}>
                <button className="btn btn-primary btn-sm" onClick={() => setOrgUserDraft({ send_welcome_email: true })} data-testid="org-add-user-btn">+ Add user to this org</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setManageOrg(null)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {modal && SPECS[modal.kind] && (
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid={SPECS[modal.kind].testid}>
            <h3>{modal.editId ? `Edit ${SPECS[modal.kind].title.replace(/^Add |^New /, "")}` : SPECS[modal.kind].title}</h3>
            {SPECS[modal.kind].fields.filter((f) => !f.hideIf || !f.hideIf(draft)).map((f) => (
              <React.Fragment key={f.k}>
                {f.type === "checkbox" ? (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400, margin: "14px 0 4px" }}>
                    <input type="checkbox" checked={!!draft[f.k]} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.checked })} style={{ width: "auto" }} data-testid={`field-${f.k}`} />
                    {f.label}
                  </label>
                ) : (
                  <>
                    <label>{f.label}</label>
                    {f.type === "select" ? (
                      <select value={draft[f.k] || ""} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })} data-testid={`field-${f.k}`}>
                        <option value="">Select…</option>
                        {f.options.map((o) => {
                          const val = typeof o === "string" ? o : o.value;
                          const lab = typeof o === "string" ? o : o.label;
                          return <option key={val} value={val}>{lab}</option>;
                        })}
                      </select>
                    ) : f.type === "textarea" ? (
                      <textarea rows={3} value={draft[f.k] || ""} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })} data-testid={`field-${f.k}`} />
                    ) : (
                      <input type={f.type || "text"} value={draft[f.k] || ""} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })} data-testid={`field-${f.k}`} />
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
            <div className="flex gap-8" style={{ marginTop: 18, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={busy === "modal"} onClick={saveModal} data-testid="modal-save">{busy === "modal" ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
